import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import FormPages from "src/EstateFlowchart/config/FormPages";
import { selectReleaseToggles } from "src/ReleaseToggles/releaseTogglesSlice";
import { RouteWithIdAndFlowchartId } from "../../../routes/types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { estateFlowchartApiClient, mapMiscellaneousFromBackend, mapMiscellaneousToBackend } from '../../client/EstateFlowchartApiClient';
import { EstateCardViewModeType } from '../../components/EstateCard/EstateCard';
import Form from "../../components/Form/Form";
import { NavItem } from "../../components/FormNav/NavItem";
import { MiscellaneousFormPages } from "../../config/MiscellaneousFormPages";
import { Miscellaneous } from "../../model/Miscellaneous";
import { getEstateFlowchartsSelector, getSelectedFlowchart, selectedFlowchartIdSelector, setGenericErrorModal, setSelectedFlowchartId, updateEstateFlowchart } from "../../slices/EstateFlowChartSlice";
import { setSaveClicked } from '../../slices/NavigationSlice';
import { emptyMiscellaneous } from "../../utils/DefUtils";
import { ValidationError } from '../../validation/ValidationTypes';
import { validateMiscellaneousForm } from '../../validation/form/MiscellaneousFormValidator';
import { EstateItemType } from "../EstateItemType";
import MiscellaneousContent from "./MiscellaneousContent";
import deepEquals from "fast-deep-equal";

const MiscellaneousForm = () => {
    const enableEstateFlowchartMVPRelease = useAppSelector(selectReleaseToggles)?.enableEstateFlowchartMVPRelease;
    const selectedFlowchart = useAppSelector(getSelectedFlowchart)
    const selectedFlowchartId = useAppSelector(selectedFlowchartIdSelector);
    const location = useLocation();
    const {id, flowchartId, trustId} = useParams<RouteWithIdAndFlowchartId>();
    const flowcharts = useAppSelector(getEstateFlowchartsSelector);

    const [linkConfig, setLinkConfig] = useState({});
    const [pageConfig, setPageConfig] = useState<NavItem[]>(MiscellaneousFormPages);
    const [miscellaneousState, setMiscellaneousState] = useState<Miscellaneous>(emptyMiscellaneous);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [formHasBeenEdited, setFormHasBeenEdited] = useState(false)
    const [initialMiscellaneous, setInitialMiscellaneous] = useState<Miscellaneous>();
    const dispatch = useAppDispatch();
    const handleCreateNewFlowchartWithMiscellaneous = async (): Promise<boolean> => {
        if (!id || flowchartId !== 'new') return false;
        const estateFlowchart = {
            flowchartId: undefined,
            flowchartName: miscellaneousState.flowchartName,
            revocableTrust: undefined,
            irrevocableTrusts: [],
            wildcardTrusts: [
                mapMiscellaneousToBackend(miscellaneousState)
            ],
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: []
        };
        const response = await estateFlowchartApiClient.createFlowchart(id, estateFlowchart);
        if (response.isSuccessStatusCode) {
            response.data.wildcardTrusts = (response.data.wildcardTrusts || []).map(mapMiscellaneousFromBackend);
            dispatch(setSelectedFlowchartId(response.data.flowchartId));
            dispatch(updateEstateFlowchart(response.data));
            const newMiscList = response.data.wildcardTrusts;
            const newMisc = newMiscList && newMiscList.length > 0 ? newMiscList[newMiscList.length - 1] : null;
            if (newMisc) {
                setMiscellaneousState(newMisc);
            }
            return true;
        } else {
            setCommunicationError(response);
        }
        return false;
    };
    const checkFormIsValid = () => {
        const {formHasErrors, errors} = validateMiscellaneousForm(FormPages.BASIC_INFORMATION.id, miscellaneousState, flowcharts, flowchartId, enableEstateFlowchartMVPRelease ?? true);
        setValidationErrors(errors);
        return !formHasErrors;
    };

    useEffect(() => {
        if (selectedFlowchart && trustId && trustId !== "new" && Array.isArray(selectedFlowchart.wildcardTrusts)) {
            const existingMisc = selectedFlowchart.wildcardTrusts.find((misc) => misc.trustId === trustId);
            if (existingMisc) {
                setMiscellaneousState(existingMisc);
                setInitialMiscellaneous(existingMisc);
                return;
            }
        }
        const modalData = location.state || {};
        let flowchartName = "";
        if (selectedFlowchart && modalData.flowchartName === "") {
            //Coming from Add estateItem, so take data from selectedFlowchart
            flowchartName = selectedFlowchart.flowchartName ?? "";
        } else if (modalData.flowchartName !== "" && modalData.flowchartName !== selectedFlowchart?.flowchartName) {
            //Coming from Add Flowchart, take modal data
            flowchartName = modalData.flowchartName;
        }
        setMiscellaneousState((prev) => ({
            ...prev,
            flowchartName: flowchartName,
        }));
    }, [location.state, selectedFlowchart, trustId]);

    useEffect(() => {
        if (
            selectedFlowchart &&
            selectedFlowchart.flowchartName !== undefined &&
            selectedFlowchart.flowchartName !== miscellaneousState.flowchartName &&
            !location.state?.flowchartName
        ) {
            setMiscellaneousState((prev) => ({
                ...prev,
                flowchartName: selectedFlowchart.flowchartName ?? "",
            }));
        }
    }, [selectedFlowchart?.flowchartName]);


    useEffect(() => {
        const hasBeenEdited = !deepEquals(initialMiscellaneous, miscellaneousState);
        setFormHasBeenEdited(hasBeenEdited);
    }, [miscellaneousState])

    const buildRequestBody = (forCreate = false): Miscellaneous => {
        return {
            ...miscellaneousState,
            trustId: forCreate ? '' : (miscellaneousState.trustId ?? trustId ?? ''),
        };
    };


    const setCommunicationError = (errorStatus: any) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    };


    const handleAddMiscellaneous = async (): Promise<boolean> => {
        if (!id || !flowchartId) return false;
        const body = buildRequestBody(true);
        const res = await estateFlowchartApiClient.createMiscellaneous(id, flowchartId, body);
        if (res.isSuccessStatusCode) {
            const newMisc: Miscellaneous = mapMiscellaneousFromBackend(res.data);
            setMiscellaneousState(newMisc);
            if (selectedFlowchart) {
                const updatedWildcardTrusts = [
                    ...(selectedFlowchart.wildcardTrusts || []),
                    newMisc
                ];
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: res.data.flowchartName,
                    wildcardTrusts: updatedWildcardTrusts
                }));
            }
            return true;
        } else {
            setCommunicationError(res);
        }
        return false;
    };

    const handleEditMiscellaneous = async (): Promise<boolean> => {
        if (!id || !flowchartId) return false;
        const body = buildRequestBody();
        const res = await estateFlowchartApiClient.editMiscellaneous(id, flowchartId, body);
        if (res.isSuccessStatusCode) {
            const updatedMisc: Miscellaneous = res.data;
            setMiscellaneousState(updatedMisc);
            if (selectedFlowchart) {
                const updatedWildcardTrusts = (selectedFlowchart.wildcardTrusts || []).map((misc) =>
                    misc.trustId === updatedMisc.trustId ? updatedMisc : misc
                );
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: res.data.flowchartName,
                    wildcardTrusts: updatedWildcardTrusts
                }));
            }
            return true;
        } else {
            setCommunicationError(res);
        }
        return false;
    };




    const saveForm = async (): Promise<boolean> => {
        dispatch(setSaveClicked(true));
        if (!checkFormIsValid()) {
            return false;
        }
        let handleSave: () => Promise<boolean>;
        if (flowchartId === "new") {
            handleSave = handleCreateNewFlowchartWithMiscellaneous;
        } else if (trustId === "new") {
            handleSave = handleAddMiscellaneous;
        } else {
            handleSave = handleEditMiscellaneous;
        }
        return handleSave();
    };

    return (
          <Form estateItem={miscellaneousState}
              estateItemType={EstateItemType.MISCELLANEOUS}
              title={EstateItemType.MISCELLANEOUS.valueOf()}
              subHeader={miscellaneousState.flowchartName || undefined}
              pageConfig={pageConfig}
              linkConfig={linkConfig}
              isFormChanged={formHasBeenEdited}
              validate={checkFormIsValid}
              saveForm={saveForm}
              validationErrors={validationErrors}
          >
            <MiscellaneousContent
                miscellaneous={miscellaneousState}
                updateMiscellaneous={setMiscellaneousState}
                pageConfig={pageConfig}
            />
        </Form>
    );
}


export default MiscellaneousForm;